import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { InterviewType, labelsByInterviewType } from '../../../shared/models/enums/interview-type.enum';
import { Presence, labelsByPresence } from '../../../shared/models/enums/presence.enum';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { InterviewsService } from '../../../shared/services/interviews.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { DisplayInterview } from './displayInterview';
import { Interview } from './interview';
import { InterviewDetailsComponent } from './interview-details/interview-details.component';

@Component({
  selector: 'app-interview',
  templateUrl: './interview.component.html',
})
export class InterviewComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  private static readonly resetInterviews: Pagination<DisplayInterview> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));
  public user: UserDto | undefined = undefined;

  @ViewChildren(SortableDirective)
  public headers: QueryList<SortableDirective>;
  public interviews: Pagination<DisplayInterview> = InterviewComponent.resetInterviews;

  public readonly interviewTypes: Map<InterviewType, string> = labelsByInterviewType;
  public readonly presences: Map<Presence, string> = labelsByPresence;

  constructor(
    private readonly interviewsService: InterviewsService,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly interviewService: InterviewsService,
    private readonly beneficiariesService: BeneficiariesService,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();
    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    await this.reloadInterviews();

    this.interviewsService.interview$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.reloadInterviews();
    });
  }

  private async reloadInterviews(): Promise<void> {
    this.interviews = await this.interviewsService.findByBeneficiaryId(this.beneficiary.id, this.interviews.options);
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    this.interviews = await this.interviewsService.findByBeneficiaryId(
      this.beneficiary.id,
      this.interviews.options.withSort(column, direction === 'desc'),
    );
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      this.interviews = await this.interviewsService.findByBeneficiaryId(this.beneficiary.id, this.interviews.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event?.target?.value) {
      const limit: number = event.target.value;
      this.interviews = await this.interviewsService.findByBeneficiaryId(this.beneficiary.id, this.interviews.options.withLimit(limit));
    }
  }

  public async openCreateModal(interview?: Interview): Promise<void> {
    if (this.user?.groups?.includes('ADMIN') || this.user?.groups?.includes('STRUCTURE') || this.user?.groups?.includes('RECEPTION')) {
      try {
        await this.interviewService.canCreate(this.beneficiary.id);
        this.openModal(interview);
      } catch (e) {
        if (e.status === 461) {
          this.toastService.show("Merci de compléter l'onglet État civil avant de saisir un contact.", 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  public async openConsultModal(interviewId?: number): Promise<void> {
    if (
      this.user?.groups?.includes('ADMIN') ||
      this.user?.groups?.includes('STRUCTURE') ||
      this.user?.groups?.includes('DIRECTION+') ||
      this.user?.groups?.includes('RECEPTION')
    ) {
      try {
        this.openModal(await this.interviewService.findByInterviewId(interviewId));
      } catch (error) {
        if (error.status === 437) {
          this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  private async openModal(interview?: Interview): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(InterviewDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: InterviewDetailsComponent = modal.componentInstance as InterviewDetailsComponent;
    await modalComponent.onInit(interview, this.beneficiary.id);
  }
}
